import { toAbsoluteUrl } from "@/utils";
import arMessages from "./messages/ar.json";
import deMessages from "./messages/de.json";
import enMessages from "./messages/en.json";
import fiMessages from "./messages/fi.json";
import frMessages from "./messages/fr.json";
import svMessages from "./messages/sv.json";
import zhMessages from "./messages/zh.json";
import { type TLanguage, type TLanguageCode } from "./types.d";

const I18N_MESSAGES = {
  en: enMessages,
  de: deMessages,
  sv: svMessages,
  fi: fiMessages,
  ar: arMessages,
  fr: frMessages,
  zh: zhMessages,
};

const I18N_CONFIG_KEY = "i18nConfig";

const I18N_LANGUAGES: readonly TLanguage[] = [
  {
    label: "English",
    code: "en",
    direction: "ltr",
    flag: toAbsoluteUrl("/media/flags/united-states.svg"),
    messages: I18N_MESSAGES.en,
  },
  {
    label: "German",
    code: "de",
    direction: "ltr",
    flag: toAbsoluteUrl("/media/flags/germany.svg"),
    messages: I18N_MESSAGES.de,
  },
  {
    label: "Swedish",
    code: "sv",
    direction: "ltr",
    flag: toAbsoluteUrl("/media/flags/sweden.svg"),
    messages: I18N_MESSAGES.sv,
  },
  {
    label: "Finnish",
    code: "fi",
    direction: "ltr",
    flag: toAbsoluteUrl("/media/flags/finland.svg"),
    messages: I18N_MESSAGES.fi,
  },
];

const I18N_DEFAULT_LANGUAGE: TLanguage = I18N_LANGUAGES[0];

/** Persisted i18n config may embed an outdated messages snapshot; always hydrate from bundles. */
const hydrateLanguage = (stored?: Partial<TLanguage> | null): TLanguage => {
  const code = (stored?.code ?? I18N_DEFAULT_LANGUAGE.code) as TLanguageCode;
  const base =
    I18N_LANGUAGES.find((lang) => lang.code === code) ?? I18N_DEFAULT_LANGUAGE;
  const messages =
    I18N_MESSAGES[code as keyof typeof I18N_MESSAGES] ?? base.messages;

  return {
    ...base,
    code: base.code,
    label: stored?.label ?? base.label,
    direction: stored?.direction ?? base.direction,
    flag: stored?.flag ?? base.flag,
    messages,
  };
};

/** Store language metadata only (not messages) to avoid stale translations in localStorage. */
const serializeLanguageForStorage = (language: TLanguage): Pick<
  TLanguage,
  "code" | "label" | "direction" | "flag"
> => ({
  code: language.code,
  label: language.label,
  direction: language.direction,
  flag: language.flag,
});

export {
  I18N_CONFIG_KEY,
  I18N_DEFAULT_LANGUAGE,
  I18N_LANGUAGES,
  I18N_MESSAGES,
  hydrateLanguage,
  serializeLanguageForStorage,
};
